import React, { Component, ReactNode, ErrorInfo } from 'react';
import { NextSeo } from 'next-seo';
import { useError } from '@olo-web/utils/common/hooks/useError';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class SEOErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[SEO Error Boundary]', error, errorInfo);
  }

  render(): JSX.Element | ReactNode {
    if (this.state.hasError) {
      // Render fallback SEO that won't crash
      return <FallbackSEO {...this.props} />;
    }

    return this.props.children;
  }
}

function FallbackSEO({ fallbackTitle, fallbackDescription }: Props) {
  const handleError = useError();

  React.useEffect(() => {
    handleError({
      error: new Error('SEO component failed to render'),
      logError: true,
      toastError: false, // Don't show toast for SEO errors
    });
  }, [handleError]);

  return (
    <NextSeo
      title={fallbackTitle || 'Order Online'}
      description={fallbackDescription || 'Order delicious food online'}
      noindex={true}
      nofollow={true}
    />
  );
}
